import { handleSearch as oinHandleSearch } from "../components/command/OINSearchComponent.jsx";
import { getOktaSessionSnapshot } from "../okta/oktaSessionStore.js";
import { dispatchOktaSswsUnauthorized } from "../okta/oktaSswsUnauthorizedDispatch.js";

async function parseJsonResponse(res) {
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    throw new Error(json?.error || `Request failed (${res.status})`);
  }
  return json;
}

/**
 * Authenticated JSON request (legacy Directory flows): path + merged SSWS session.
 */
async function executeOktaHttpPath(path, options = {}) {
  const { method = "POST", body = {} } = options;
  const s = getOktaSessionSnapshot();
  const merged = {
    ...body,
    oktaUrl:
      body.oktaUrl != null && String(body.oktaUrl).trim() ? String(body.oktaUrl).trim() : s.orgUrl,
    apiToken:
      body.apiToken != null && String(body.apiToken).trim() ? String(body.apiToken).trim() : s.apiToken,
  };
  if (!merged.oktaUrl?.trim() || !merged.apiToken?.trim()) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or the tenant fields.");
  }

  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(merged),
  });
  return parseJsonResponse(res);
}

/**
 * Legacy path-based helper, or AI form payload (no round-trip to the architect).
 * - String first arg: POST to `path` with body + tenant session (Directory scans).
 * - Object first arg: `executeOktaAction(formData, { activeTask })` → users / ServiceNow / catalog search.
 *
 * @param {string | Record<string, unknown>} pathOrFormData
 * @param {{ method?: string, body?: object, activeTask?: string | null }} [options]
 */
export async function executeOktaAction(pathOrFormData, options = {}) {
  if (typeof pathOrFormData === "string") {
    return executeOktaHttpPath(pathOrFormData, options);
  }
  return executeOktaFromFormData(pathOrFormData, options);
}

/**
 * OIN / public catalog search by `q` (proxied via /api/idp/okta/oin-search; optional SSWS for tenant-catalog fallback).
 * Single POST to the OIN proxy route via {@link oinHandleSearch} only — never `/api/ai/chat`.
 * No automatic retries on 429.
 *
 * @param {string} query
 * @param {{ oktaUrl?: string | null, apiToken?: string | null } | null} [sessionOverride]
 */
export async function searchCatalogApps(query, sessionOverride = null) {
  return oinHandleSearch(query, sessionOverride);
}

/**
 * Create a user in Okta (POST /api/v1/users). Token + org come from in-memory Okta tenant session;
 * the server forwards to Okta with Authorization: SSWS (API token from the tenant session).
 * @param {{ profile: { firstName: string, lastName: string, email: string, login: string } }} body
 */
export async function createOktaUser(body) {
  const s = getOktaSessionSnapshot();
  const res = await fetch("/api/idp/okta/users", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oktaUrl: s.orgUrl,
      apiToken: s.apiToken,
      profile: body.profile,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    throw err;
  }
  return json;
}

const GROUP_RULES_DEFAULT_TIMEOUT_MS = 5000;

/**
 * List group rules from Okta (render locally; do not round-trip rule JSON through the architect).
 * @param {{ timeoutMs?: number }} [options]
 */
export async function listGroupRules(options = {}) {
  const { timeoutMs = GROUP_RULES_DEFAULT_TIMEOUT_MS } = options;
  const s = getOktaSessionSnapshot();
  if (!s.orgUrl?.trim() || !s.apiToken?.trim()) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or the tenant fields.");
  }
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch("/api/idp/okta/group-rules", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      signal: controller.signal,
      body: JSON.stringify({ oktaUrl: s.orgUrl, apiToken: s.apiToken }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) dispatchOktaSswsUnauthorized();
      const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
      err.errorCode = json.errorCode;
      err.errorCauses = json.errorCauses;
      err.status = res.status;
      throw err;
    }
    return json;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * GET /api/v1/authenticators?status=ACTIVE (via server proxy). Identity Engine orgs.
 */
export async function listActiveAuthenticators() {
  const s = getOktaSessionSnapshot();
  if (!s.orgUrl?.trim() || !s.apiToken?.trim()) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or the tenant fields.");
  }
  const res = await fetch("/api/idp/okta/authenticators/active", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ oktaUrl: s.orgUrl, apiToken: s.apiToken }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    throw err;
  }
  return json;
}

/**
 * Structured authenticator summary for architect prompt (no raw arrays).
 */
export async function fetchAuthenticatorsSummary() {
  const s = getOktaSessionSnapshot();
  if (!s.orgUrl?.trim() || !s.apiToken?.trim()) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or the tenant fields.");
  }
  const res = await fetch("/api/idp/okta/context/authenticators-summary", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ oktaUrl: s.orgUrl, apiToken: s.apiToken }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    throw new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
  }
  return json;
}

/**
 * Structured group rules summary for architect prompt (no raw arrays).
 */
export async function fetchGroupRulesSummary() {
  const s = getOktaSessionSnapshot();
  if (!s.orgUrl?.trim() || !s.apiToken?.trim()) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or the tenant fields.");
  }
  const res = await fetch("/api/idp/okta/context/group-rules-summary", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ oktaUrl: s.orgUrl, apiToken: s.apiToken }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    throw new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
  }
  return json;
}

/**
 * Create group from embedded proposal JSON (server extracts profile.name group).
 */
export async function applyProposalGroup(proposalBody) {
  const s = getOktaSessionSnapshot();
  const res = await fetch("/api/idp/okta/proposals/group", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oktaUrl: s.orgUrl,
      apiToken: s.apiToken,
      proposalBody,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    throw err;
  }
  return json;
}

/**
 * Create sign-on policy from embedded proposal JSON.
 */
export async function applyProposalPolicy(proposalBody) {
  const s = getOktaSessionSnapshot();
  const res = await fetch("/api/idp/okta/proposals/policy", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oktaUrl: s.orgUrl,
      apiToken: s.apiToken,
      proposalBody,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    throw err;
  }
  return json;
}

/**
 * Create ServiceNow from OIN via POST /api/v1/apps (server uses template OKTA_OIN_SERVICENOW_NAME or "servicenow").
 * @param {{ instanceName: string, subdomain: string, baseUrl: string, label?: string }} fields
 */
/**
 * Create a SAML 2.0 app from Okta template `template_saml_2_0`.
 * @param {{ appName?: string, ssoUrl?: string, audience?: string, attributes?: unknown[], projectId?: string }} fields
 * @param {{ oktaUrl?: string | null, apiToken?: string | null } | null} [tenantOverride]
 */
export async function createCustomSamlApp(fields, tenantOverride = null) {
  const s = getOktaSessionSnapshot();
  const oktaUrl = String(tenantOverride?.oktaUrl ?? s.orgUrl ?? "").trim();
  const apiToken = String(tenantOverride?.apiToken ?? s.apiToken ?? "").trim();
  if (!oktaUrl || !apiToken) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or reconnect your org.");
  }
  const res = await fetch("/api/idp/okta/apps/saml", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oktaUrl,
      apiToken,
      appName: fields.appName,
      ssoUrl: fields.ssoUrl,
      audience: fields.audience,
      attributes: fields.attributes,
      projectId: fields.projectId,
      nameIdFormat: fields.nameIdFormat ?? fields.nameIDFormat,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    err.detail = json.detail;
    throw err;
  }
  return json;
}

/**
 * Fetch OIN catalog app schema (POST /api/idp/okta/oin-schema) — includes `curatedUiSchema` when defined server-side.
 * @param {string} appName — OIN catalog template key (e.g. salesforce)
 * @param {{ oktaUrl?: string | null, apiToken?: string | null } | null} [tenantOverride]
 */
export async function fetchOinAppSchema(appName, tenantOverride = null) {
  const s = getOktaSessionSnapshot();
  const oktaUrl = String(tenantOverride?.oktaUrl ?? s.orgUrl ?? "").trim();
  const apiToken = String(tenantOverride?.apiToken ?? s.apiToken ?? "").trim();
  if (!oktaUrl || !apiToken) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or reconnect your org.");
  }
  const name = appName != null ? String(appName).trim() : "";
  if (!name) {
    throw new Error("appName is required for OIN schema.");
  }
  const res = await fetch("/api/idp/okta/oin-schema", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ oktaUrl, apiToken, appName: name }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    err.detail = json.detail;
    throw err;
  }
  return json;
}

/**
 * Create an OIN catalog app (POST /api/idp/okta/apps/oin) — schema-driven; pass settings from /api/idp/okta/oin-schema when needed.
 * @param {{ appName?: string, templateName?: string, label?: string, signOnMode?: string, settings?: { general?: object, sso?: object }, projectId?: string }} fields
 * @param {{ oktaUrl?: string | null, apiToken?: string | null } | null} [tenantOverride]
 */
export async function installOinTemplateApp(fields, tenantOverride = null) {
  const s = getOktaSessionSnapshot();
  const oktaUrl = String(tenantOverride?.oktaUrl ?? s.orgUrl ?? "").trim();
  const apiToken = String(tenantOverride?.apiToken ?? s.apiToken ?? "").trim();
  if (!oktaUrl || !apiToken) {
    throw new Error("Okta Org URL and API token are required. Use Connect Okta or reconnect your org.");
  }
  const appName = fields.appName || fields.templateName;
  const res = await fetch("/api/idp/okta/apps/oin", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oktaUrl,
      apiToken,
      appName,
      templateName: fields.templateName,
      label: fields.label,
      signOnMode: fields.signOnMode,
      settings: fields.settings,
      projectId: fields.projectId,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    err.detail = json.detail;
    throw err;
  }
  return json;
}

export async function createServiceNowOinApp(fields) {
  const s = getOktaSessionSnapshot();
  const res = await fetch("/api/idp/okta/apps/servicenow", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      oktaUrl: s.orgUrl,
      apiToken: s.apiToken,
      instanceName: fields.instanceName,
      subdomain: fields.subdomain,
      baseUrl: fields.baseUrl,
      label: fields.label,
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    if (res.status === 401) dispatchOktaSswsUnauthorized();
    const err = new Error(json.errorSummary || json.error || `Request failed (${res.status})`);
    err.errorCode = json.errorCode;
    err.errorCauses = json.errorCauses;
    err.status = res.status;
    throw err;
  }
  return json;
}

async function executeOktaFromFormData(formData, options = {}) {
  const fd = formData && typeof formData === "object" ? formData : {};
  if (fd._kind === "oin_catalog_search") {
    const q = String(fd.q ?? fd.query ?? "").trim();
    return searchCatalogApps(q);
  }

  const { activeTask } = options;
  if (activeTask === "manual_user_create") {
    const email = String(fd.email || "").trim();
    const profile = {
      firstName: String(fd.firstName || "").trim(),
      lastName: String(fd.lastName || "").trim(),
      email,
      login: String(fd.login || email || "").trim(),
    };
    return createOktaUser({ profile });
  }

  if (activeTask === "oin_servicenow") {
    return createServiceNowOinApp({
      instanceName: fd.instanceName,
      subdomain: fd.subdomain,
      baseUrl: fd.baseUrl,
      label: fd.label,
    });
  }

  if (activeTask === "CUSTOM_SAML") {
    return createCustomSamlApp({
      appName: fd.appName ?? fd.applicationName ?? fd.label ?? fd.name,
      ssoUrl: fd.ssoUrl ?? fd.ssoAcsUrl ?? fd.acsUrl,
      audience: fd.audience,
      attributes: fd.attributes,
      projectId: fd.projectId,
      nameIdFormat: fd.nameIdFormat ?? fd.nameIDFormat,
    });
  }

  throw new Error("Direct Okta execution is not available for this task.");
}
